<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import KanbanApi from 'dashboard/api/kanban';
import ConversationApi from 'dashboard/api/conversations';
import { dynamicTime } from 'shared/helpers/timeHelper';

const store = useStore();
const router = useRouter();
const { accountScopedRoute } = useAccount();

const inboxes = useMapGetter('inboxes/getInboxes');
const currentUser = useMapGetter('getCurrentUser');
const isSuperAdmin = computed(() => currentUser.value?.id === 1);

// Mesmas regras do backend (labelable.rb):
// - marcadoras coexistem com o status -> não são colunas de arrasto (nem origem
//   nem destino); o card delas também vive numa coluna de status.
// - protegidas só o super admin (id 1) remove -> só ele arrasta PRA FORA delas.
const MARKER_LABELS = [
  'clt_link_simule_enviado',
  'clt_pagamento_efetuado',
  'clt_operacional',
];
const PROTECTED_LABELS = ['clt_pago', 'clt_pagamento_efetuado'];

const columns = ref([]);
const pageSize = ref(25);
const isLoading = ref(false);
const loadingMore = ref({});
const selectedInboxId = ref(null);
const inboxMenuOpen = ref(false);

// Drag & drop + modal de confirmação
const dragging = ref(null); // { conv, from }
const dropTarget = ref(null); // title da coluna sob o cursor
const confirmMove = ref(null); // { conv, from, to, saving }

const selectedInboxName = computed(() => {
  if (!selectedInboxId.value) return 'TODOS';
  const i = inboxes.value.find(x => x.id === selectedInboxId.value);
  return i?.name || 'TODOS';
});

const totalCards = computed(() =>
  columns.value.reduce((sum, c) => sum + c.total, 0)
);

const canDragFrom = title =>
  !MARKER_LABELS.includes(title) &&
  (isSuperAdmin.value || !PROTECTED_LABELS.includes(title));

const canDropTo = title => !MARKER_LABELS.includes(title);

const fetchBoard = async () => {
  isLoading.value = true;
  try {
    const { data } = await KanbanApi.getBoard({
      inboxId: selectedInboxId.value,
    });
    columns.value = data.columns || [];
    pageSize.value = data.page_size || 25;
  } catch {
    useAlert('Não foi possível carregar o Kanban.');
  } finally {
    isLoading.value = false;
  }
};

const loadMore = async column => {
  if (loadingMore.value[column.title]) return;
  loadingMore.value = { ...loadingMore.value, [column.title]: true };
  try {
    const { data } = await KanbanApi.getColumn({
      label: column.title,
      offset: column.conversations.length,
      inboxId: selectedInboxId.value,
    });
    const seen = new Set(column.conversations.map(c => c.id));
    const novos = (data.conversations || []).filter(c => !seen.has(c.id));
    column.conversations.push(...novos);
  } catch {
    useAlert('Não foi possível carregar mais leads.');
  } finally {
    loadingMore.value = { ...loadingMore.value, [column.title]: false };
  }
};

const selectInbox = id => {
  selectedInboxId.value = id;
  inboxMenuOpen.value = false;
  fetchBoard();
};

const toggleInboxMenu = () => {
  inboxMenuOpen.value = !inboxMenuOpen.value;
};

const closeInboxMenu = e => {
  if (!e.target.closest('.kanban-inbox-filter')) {
    inboxMenuOpen.value = false;
  }
};

const openConversation = conv => {
  router.push(
    accountScopedRoute('inbox_conversation', { conversation_id: conv.id })
  );
};

// --- Drag & drop ---
const onDragStart = (e, conv, fromTitle) => {
  dragging.value = { conv, from: fromTitle };
  e.dataTransfer.effectAllowed = 'move';
  e.dataTransfer.setData('text/plain', String(conv.id));
};

const onDragEnd = () => {
  dragging.value = null;
  dropTarget.value = null;
};

const onDragEnter = title => {
  if (!dragging.value) return;
  dropTarget.value =
    title !== dragging.value.from && canDropTo(title) ? title : null;
};

const onDrop = title => {
  const drag = dragging.value;
  dropTarget.value = null;
  if (!drag || title === drag.from || !canDropTo(title)) return;
  confirmMove.value = {
    conv: drag.conv,
    from: drag.from,
    to: title,
    saving: false,
  };
};

const cancelMove = () => {
  confirmMove.value = null;
};

const applyMove = async () => {
  const move = confirmMove.value;
  if (!move || move.saving) return;
  move.saving = true;
  try {
    // Envia só a etiqueta nova: o backend (update_labels) troca o status e
    // preserva as marcadoras; a atividade fica registrada no chat da conversa.
    await ConversationApi.updateLabels(move.conv.id, [move.to]);
    const fromCol = columns.value.find(c => c.title === move.from);
    const toCol = columns.value.find(c => c.title === move.to);
    if (fromCol) {
      fromCol.conversations = fromCol.conversations.filter(
        c => c.id !== move.conv.id
      );
      fromCol.total = Math.max(0, fromCol.total - 1);
    }
    if (toCol && !toCol.conversations.some(c => c.id === move.conv.id)) {
      toCol.conversations.unshift(move.conv);
      toCol.total += 1;
    }
    columns.value = columns.value.filter(c => c.total > 0);
    useAlert(`Status alterado: ${move.from} → ${move.to}`);
    confirmMove.value = null;
  } catch {
    useAlert('Não foi possível alterar o status. Tente novamente.');
    move.saving = false;
  }
};

const enteredAt = conv =>
  conv.last_activity_at ? dynamicTime(conv.last_activity_at) : '';

const initials = name =>
  (name || '?')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(w => w[0])
    .join('')
    .toUpperCase();

onMounted(() => {
  store.dispatch('inboxes/get');
  document.addEventListener('click', closeInboxMenu);
  fetchBoard();
});
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-hidden bg-n-background">
    <header
      class="flex items-center justify-between px-6 py-3 border-b border-n-weak bg-n-solid-1"
    >
      <div class="flex items-center gap-3">
        <span class="i-lucide-kanban-square size-5 text-n-brand" />
        <h1 class="text-lg font-semibold text-n-slate-12">Kanban</h1>
        <span
          v-if="!isLoading"
          class="px-2 py-0.5 text-xs font-medium rounded-full bg-n-alpha-2 text-n-slate-11"
        >
          {{ columns.length }} etiquetas · {{ totalCards.toLocaleString('pt-BR') }} leads
        </span>
      </div>

      <div class="flex items-center gap-2">
        <button
          type="button"
          class="flex items-center gap-2 px-3 py-2 text-sm font-medium border rounded-md border-n-weak bg-n-alpha-1 hover:bg-n-alpha-2 text-n-slate-12 transition-colors"
          @click="fetchBoard"
        >
          <span
            class="i-lucide-refresh-cw size-4 text-n-slate-11"
            :class="{ 'animate-spin': isLoading }"
          />
          Atualizar
        </button>

        <div class="kanban-inbox-filter relative">
          <button
            type="button"
            class="flex items-center gap-2 px-3 py-2 text-sm font-medium border rounded-md border-n-weak bg-n-alpha-1 hover:bg-n-alpha-2 text-n-slate-12 transition-colors"
            @click="toggleInboxMenu"
          >
            <span class="i-lucide-mailbox size-4 text-n-slate-11" />
            <span>{{ selectedInboxName }}</span>
            <span class="i-lucide-chevron-down size-4 text-n-slate-11" />
          </button>
          <div
            v-if="inboxMenuOpen"
            class="absolute right-0 z-10 mt-2 overflow-y-auto rounded-md shadow-lg bg-n-solid-1 border border-n-weak min-w-[260px] max-h-[400px]"
          >
            <button
              type="button"
              class="block w-full px-4 py-2 text-sm text-left text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
              :class="{ 'bg-n-alpha-2 font-semibold': !selectedInboxId }"
              @click="selectInbox(null)"
            >
              TODOS
            </button>
            <button
              v-for="inbox in inboxes"
              :key="inbox.id"
              type="button"
              class="block w-full px-4 py-2 text-sm text-left text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
              :class="{
                'bg-n-alpha-2 font-semibold': selectedInboxId === inbox.id,
              }"
              @click="selectInbox(inbox.id)"
            >
              {{ inbox.name }}
            </button>
          </div>
        </div>
      </div>
    </header>

    <div
      v-if="isLoading"
      class="flex items-center justify-center flex-1 gap-2 text-sm text-n-slate-10"
    >
      <span class="i-lucide-loader-2 size-4 animate-spin" />
      Carregando leads…
    </div>

    <div
      v-else-if="!columns.length"
      class="flex items-center justify-center flex-1 text-sm text-n-slate-10"
    >
      Nenhum lead com etiqueta encontrado.
    </div>

    <div v-else class="flex-1 px-5 py-4 overflow-x-auto">
      <div class="flex h-full gap-3">
        <section
          v-for="column in columns"
          :key="column.title"
          class="flex flex-col w-72 overflow-hidden rounded-xl shrink-0 border bg-n-solid-1 transition-shadow"
          :class="
            dropTarget === column.title
              ? 'border-n-brand shadow-lg'
              : 'border-n-weak'
          "
          @dragover.prevent
          @dragenter.prevent="onDragEnter(column.title)"
          @drop.prevent="onDrop(column.title)"
        >
          <div
            class="h-1 shrink-0"
            :style="{ backgroundColor: column.color || '#1f93ff' }"
          />
          <div class="flex items-center justify-between gap-2 px-3 py-2.5">
            <div class="flex items-center min-w-0 gap-2">
              <span
                class="rounded-full size-2.5 shrink-0"
                :style="{ backgroundColor: column.color || '#1f93ff' }"
              />
              <span
                class="text-xs font-semibold tracking-wide uppercase truncate text-n-slate-12"
              >
                {{ column.title.replace(/^clt_/, '').replaceAll('_', ' ') }}
              </span>
              <span
                v-if="!canDragFrom(column.title)"
                class="i-lucide-lock size-3 shrink-0 text-n-slate-9"
                title="Etiqueta protegida ou marcadora — sem arrasto"
              />
            </div>
            <span
              class="px-2 py-0.5 text-xs font-bold rounded-full bg-n-alpha-2 text-n-slate-11 shrink-0"
            >
              {{ column.total.toLocaleString('pt-BR') }}
            </span>
          </div>

          <div class="flex-1 px-2 pb-2 space-y-2 overflow-y-auto">
            <article
              v-for="conv in column.conversations"
              :key="conv.id"
              class="p-3 transition-all border rounded-lg group bg-n-solid-2 border-n-weak hover:border-n-slate-7 hover:shadow-sm"
              :class="[
                canDragFrom(column.title) ? 'cursor-grab' : 'cursor-pointer',
                dragging?.conv?.id === conv.id ? 'opacity-40' : '',
              ]"
              :draggable="canDragFrom(column.title)"
              @dragstart="onDragStart($event, conv, column.title)"
              @dragend="onDragEnd"
              @click="openConversation(conv)"
            >
              <div class="flex items-center gap-2.5">
                <span
                  class="flex items-center justify-center text-[11px] font-bold text-white rounded-full size-8 shrink-0"
                  :style="{ backgroundColor: column.color || '#1f93ff' }"
                >
                  {{ initials(conv.name) }}
                </span>
                <div class="min-w-0">
                  <div class="text-sm font-semibold truncate text-n-slate-12">
                    {{ conv.name }}
                  </div>
                  <div
                    v-if="conv.phone"
                    class="text-xs truncate text-n-slate-11"
                  >
                    {{ conv.phone }}
                  </div>
                </div>
              </div>

              <div
                class="flex items-center justify-between mt-2 text-[11px] text-n-slate-10"
              >
                <span v-if="enteredAt(conv)" class="flex items-center gap-1">
                  <span class="i-lucide-clock size-3" />
                  {{ enteredAt(conv) }}
                </span>
                <span class="flex items-center gap-2">
                  <span
                    v-if="conv.assignee"
                    class="truncate max-w-[100px]"
                    :title="conv.assignee"
                  >
                    {{ conv.assignee }}
                  </span>
                  <span class="font-medium">#{{ conv.id }}</span>
                </span>
              </div>
            </article>

            <button
              v-if="column.conversations.length < column.total"
              type="button"
              class="flex items-center justify-center w-full gap-1.5 px-3 py-2 text-xs font-medium border border-dashed rounded-lg border-n-weak text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12 transition-colors"
              @click="loadMore(column)"
            >
              <span
                v-if="loadingMore[column.title]"
                class="i-lucide-loader-2 size-3.5 animate-spin"
              />
              <span v-else class="i-lucide-chevron-down size-3.5" />
              Carregar mais
              ({{ (column.total - column.conversations.length).toLocaleString('pt-BR') }} restantes)
            </button>
          </div>
        </section>
      </div>
    </div>

    <!-- Modal de confirmação do arrasto -->
    <div
      v-if="confirmMove"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      @click.self="cancelMove"
    >
      <div
        class="w-full max-w-md p-6 mx-4 rounded-xl shadow-2xl bg-n-solid-1 border border-n-weak"
      >
        <div class="flex items-center gap-3">
          <span
            class="flex items-center justify-center rounded-full size-10 bg-n-alpha-2 shrink-0"
          >
            <span class="i-lucide-arrow-right-left size-5 text-n-brand" />
          </span>
          <h2 class="text-base font-semibold text-n-slate-12">
            Tem certeza que deseja alterar o Status desse cliente?
          </h2>
        </div>

        <div class="mt-4 text-sm text-n-slate-11">
          <div class="font-semibold text-n-slate-12">
            {{ confirmMove.conv.name }}
            <span class="font-normal text-n-slate-10">
              · #{{ confirmMove.conv.id }}
            </span>
          </div>
          <div class="flex items-center gap-2 mt-2">
            <span
              class="px-2 py-0.5 text-xs font-medium rounded-full bg-n-alpha-2 text-n-slate-11"
            >
              {{ confirmMove.from }}
            </span>
            <span class="i-lucide-arrow-right size-3.5 text-n-slate-10" />
            <span
              class="px-2 py-0.5 text-xs font-medium text-white rounded-full bg-n-brand"
            >
              {{ confirmMove.to }}
            </span>
          </div>
        </div>

        <div class="flex justify-between gap-3 mt-6">
          <button
            type="button"
            class="px-4 py-2 text-sm font-medium border rounded-md border-n-weak text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
            :disabled="confirmMove.saving"
            @click="cancelMove"
          >
            NÃO
          </button>
          <button
            type="button"
            class="flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white rounded-md bg-n-brand hover:brightness-110 transition-all disabled:opacity-60"
            :disabled="confirmMove.saving"
            @click="applyMove"
          >
            <span
              v-if="confirmMove.saving"
              class="i-lucide-loader-2 size-4 animate-spin"
            />
            SIM
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
